import { Component, type ErrorInfo, type ReactNode } from "react";
import { CHUNK_RELOAD_KEY, chunkUrlFrom, isChunkLoadError, shouldReloadForChunk } from "@greg-chick/shared";
import { reportError } from "../lib/errorReport";

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  /** 화면 조각을 못 받았다 — 고장이 아니라 회선이다(아래 render 참고) */
  chunk: boolean;
}

/**
 * 최상위 에러 경계 — 런타임 오류로 흰 화면이 되지 않게 친절한 폴백을 보여준다.
 * 영유아·보호자 대상이라 메시지는 쉽고 차분하게. 새로고침으로 회복.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, chunk: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, chunk: isChunkLoadError(error) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 원문은 콘솔에만. 밖으로는 **자유 문자열 없는** 익명 신호만 나간다(불변식 1).
    // 이게 없으면 아이 화면이 하얗게 떠도 운영자는 영영 모른다.
    console.error("[앱 오류] 컴포넌트", info.componentStack);
    reportError({ error, source: "boundary" });

    /**
     * 📦 **배포 직후 조각을 못 받은 것이면 우리가 한 번 다시 연다.**
     *
     * 새로 배포한 직후 몇 분 동안, 가까운 서버에 아직 안 퍼진 조각을 부르면
     * Pages 가 200 으로 index.html 을 준다 — 브라우저는 「자바스크립트인 줄 알았는데
     * 문서가 왔다」로 실패한다. 아이는 잘못한 게 없는데 오류 화면을 본다.
     *
     * 한 번 다시 열면 거의 항상 낫는다. 그러니 아이에게 [다시 시도]를 누르게 할 이유가 없다.
     * 🔴 **한 번만** — 안 낫는 진짜 고장에서 깜빡이기만 하는 것이 제일 나쁘다(판정은 shared).
     *
     * 신고는 **먼저** 보낸다 — 다시 열면 이 화면은 사라지므로, 그 전에 남겨야 운영자가 안다.
     */
    let mark: { get: () => string | null; set: (v: string) => void } | null = null;
    try {
      const ss = window.sessionStorage;
      mark = {
        get: () => ss.getItem(CHUNK_RELOAD_KEY),
        set: (v) => ss.setItem(CHUNK_RELOAD_KEY, v),
      };
    } catch {
      // 저장이 막힌 브라우저(사생활 보호 모드 등) — 기억을 못 하면 다시 열지 않는다
      mark = null;
    }
    if (!shouldReloadForChunk(error, mark)) return;

    /**
     * 🔴 **그냥 다시 열면 안 낫는다** (2026-09-06에 알았다).
     *
     * Pages 는 아직 안 퍼진 조각에 200 + `immutable, max-age=1년` 으로 index.html 을 준다
     * (curl 로 실측). 브라우저는 그 **HTML 을 그 .js 주소의 답으로 1년치 저장**한다.
     * 그래서 다시 열어도 **제 컴퓨터 사본**을 읽어 똑같이 실패하고, 두 번째부터는
     * 「이미 해 봤다」로 오류 화면이 남는다 — 조각이 서버에 다 퍼진 뒤에도 그 아이만 계속.
     * 운영에서 `/v` 를 세 번 열어 두 번 그랬다.
     *
     * 그래서 다시 열기 **전에** 그 주소를 `cache: "reload"` 로 한 번 받아 사본을 덮는다.
     * 받아 오든 못 받아 오든 **반드시 다시 연다** — 여기서 멈추면 오류 화면이 그대로 남는다.
     */
    const url = chunkUrlFrom(error);
    if (!url) {
      window.location.reload();
      return;
    }
    void fetch(url, { cache: "reload" }).catch(() => undefined).finally(() => window.location.reload());
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    /**
     * 📶 **조각을 못 받은 것이면 「다시 시도하면 금방 괜찮아져요」가 틀린 말이다** (2026-10-28).
     * 한 번 다시 열어도(위) 또 못 받았다 = 대개 끊겼거나 매달린 와이파이다. 끊긴 동안은 몇 번을 눌러도 같다.
     * 끊긴 곳에서 처음 여는 화면은 원래 못 연다(sw.js 의 약속: 한 번 본 것만) — 그 약속을 보호자에게 그대로 말한다.
     */
    if (this.state.chunk) {
      return (
        <main className="app-error" role="alert">
          <div className="app-error__box">
            <span className="app-error__emoji" aria-hidden="true">
              📶
            </span>
            <h1>이 화면을 받아 오지 못했어요</h1>
            <p>인터넷이 끊겼거나 약한 것 같아요. 연결된 뒤에 다시 시도해 주세요.</p>
            <p>한 번 열어 본 화면과 「담아 둔 책」은 끊겨도 볼 수 있어요.</p>
            <div className="app-error__actions">
              <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
                다시 시도
              </button>
              <a className="btn" href="/">
                처음으로
              </a>
            </div>
          </div>
        </main>
      );
    }
    return (
      <main className="app-error" role="alert">
        <div className="app-error__box">
          <span className="app-error__emoji" aria-hidden="true">
            🐤
          </span>
          <h1>앗, 잠깐 문제가 생겼어요</h1>
          <p>다시 시도하면 대부분 금방 괜찮아져요.</p>
          <div className="app-error__actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => window.location.reload()}
            >
              다시 시도
            </button>
            <a className="btn" href="/">
              처음으로
            </a>
          </div>
        </div>
      </main>
    );
  }
}
